import { FC, useState } from 'react';
import { ClipboardDocumentIcon, ClipboardDocumentCheckIcon, ChevronDownIcon, ChevronUpIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { Tooltip } from './Tooltip';

interface DerDevice {
  sn: string;
  lastSeen: string;
}

interface InstallationSettings {
  voltage: 110 | 230;
  fuseSize: number;
  onSettingsChange: (settings: { voltage: 110 | 230; fuseSize: number }) => void;
}

interface DerMetadataAccordionProps {
  devices: DerDevice[];
  className?: string;
  installationSettings?: InstallationSettings;
  defaultExpanded?: boolean;
}

const FUSE_SIZE_OPTIONS = [6, 10, 13, 16, 20, 25, 32, 40, 50, 63] as const;

export const DerMetadataAccordion: FC<DerMetadataAccordionProps> = ({ 
  devices, 
  className = '', 
  installationSettings,
  defaultExpanded = false
}) => {
  const [copiedSerial, setCopiedSerial] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // Function to truncate serial number
  const truncateSerial = (serial: string) => {
    if (serial.length <= 12) return serial;
    return `${serial.slice(0, 6)}...${serial.slice(-4)}`;
  };

  // Function to handle copy
  const handleCopy = async (serial: string) => {
    try {
      await navigator.clipboard.writeText(serial);
      setCopiedSerial(serial);
      setTimeout(() => setCopiedSerial(null), 2000); // Reset after 2 seconds
    } catch (err) {
      // Silently fail if copy is not supported
    }
  };

  // Toggle accordion expand/collapse
  const toggleAccordion = () => {
    setIsExpanded(!isExpanded);
  };

  // Calculate max height for animation based on number of devices and whether installation settings are present
  const getMaxHeight = () => {
    const deviceHeight = devices.length * 120; // Base height for devices
    const installationSettingsHeight = installationSettings ? 120 : 0; // Additional height for installation settings
    return deviceHeight + installationSettingsHeight;
  };
  
  // Function to format the date safely
  const formatLastSeen = (lastSeenStr: string) => {
    try {
      const date = new Date(lastSeenStr);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Unknown';
      }
      return date.toLocaleString();
    } catch (e) {
      return 'Unknown';
    }
  };

  // Helper function to safely format numbers
  const formatNumber = (value: number | undefined | null, decimals: number = 2) => {
    if (value === undefined || value === null) return '0.000';
    return value.toFixed(decimals);
  };

  // Calculate maximum power based on settings
  const calculatedMaxPowerKw = installationSettings 
    ? (3 * installationSettings.voltage * installationSettings.fuseSize) / 1000
    : 0;

  return (
    <div className={`w-full ${className}`}>
      <button 
        onClick={toggleAccordion}
        className="w-full flex items-center justify-between p-2 hover:bg-gray-200 dark:hover:bg-[#292929] rounded-lg transition-colors"
        aria-expanded={isExpanded}
      >
        <span className="text-sm text-gray-400 font-medium">Meta Data</span>
        {isExpanded ? (
          <ChevronUpIcon className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronDownIcon className="w-4 h-4 text-gray-400" />
        )}
      </button>
      
      <div 
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{ 
          maxHeight: isExpanded ? `${getMaxHeight()}px` : '0px',
          opacity: isExpanded ? 1 : 0
        }}
      >
        {devices.map((device, index) => (
          <div 
            key={device.sn || index}
            className="p-2 flex flex-col md:flex-row md:items-center md:justify-between space-y-2 md:space-y-0 border-b border-gray-800 last:border-b-0"
          >
            <div className="flex items-center">
              <p className="text-sm text-gray-400 truncate">Serial number: {truncateSerial(device.sn)}</p>
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Prevent accordion toggle
                  handleCopy(device.sn);
                }}
                className="p-1 hover:bg-gray-200 dark:hover:bg-[#292929] rounded-lg transition-colors shrink-0 ml-1"
                title="Copy serial number"
              >
                {copiedSerial === device.sn ? (
                  <ClipboardDocumentCheckIcon className="w-4 h-4 text-green-500" />
                ) : (
                  <ClipboardDocumentIcon className="w-4 h-4 text-[#00FF84]" />
                )}
              </button>
            </div>
            <p className="text-sm text-gray-400">
              Last seen: {formatLastSeen(device.lastSeen)}
            </p>
          </div>
        ))}

        {/* Installation Settings Section */}
        {installationSettings && (
          <div className="p-3 border-t border-gray-800">
            <div className="flex items-center gap-2 mb-3">
              <h3 className="text-sm text-gray-400 font-medium">Installation Settings</h3>
              <Tooltip text="Electrical installation settings\nfor this meter">
                <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-pointer" />
              </Tooltip>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
              <div className="flex items-center gap-2">
                <label className="text-gray-400 text-xs min-w-[50px]">Voltage:</label>
                <div className="relative">
                  <select
                    className="bg-[#131313] border border-[#292929] rounded-[6px] pl-3 pr-8 py-1 text-gray-200 text-xs appearance-none"
                    value={installationSettings.voltage}
                    onChange={(e) => installationSettings.onSettingsChange({
                      voltage: parseInt(e.target.value) as 110 | 230,
                      fuseSize: installationSettings.fuseSize
                    })}
                  >
                    <option value="110">110V</option>
                    <option value="230">230V</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <svg className="fill-current h-3 w-3 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-gray-400 text-xs min-w-[50px]">Fuse:</label>
                <div className="relative">
                  <select
                    className="bg-[#131313] border border-[#292929] rounded-[6px] pl-3 pr-8 py-1 text-gray-200 text-xs appearance-none"
                    value={installationSettings.fuseSize}
                    onChange={(e) => installationSettings.onSettingsChange({
                      voltage: installationSettings.voltage,
                      fuseSize: parseInt(e.target.value)
                    })}
                  >
                    {FUSE_SIZE_OPTIONS.map(size => (
                      <option key={size} value={size}>{size}A</option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <svg className="fill-current h-3 w-3 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-400 text-xs min-w-[50px]">Max Power:</span>
                <span className="text-xs font-medium text-[#00FF84]">{formatNumber(calculatedMaxPowerKw, 1)} kW</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}; 